import React, { CSSProperties, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { Box, GridLegacy, Typography } from "@mui/material";
import TranslationWrapper, { getTranslation } from "../../../../components/TranslationWrapper/TranslationWrapper";
import { FilterRibbon } from "../../../../components/FilterRibbon/FilterRibbon";
import Checkbox from "@mui/material/Checkbox";
import AuthenticationWrapper from "../../../../../beheer/auth/AuthenticationWrapper";
import LiveInterfaceEnvironment from "../../../../components/LiveInterfaceCustomComponents/LiveInterface";
import { JamezzStylesComponent } from "./JamezzStyles/JamezzStylesComponent";
import { ribbonComponents_KioskV5 } from "./JamezzStyles/data/ribbon/kioskRibbon";
import { ribbonComponents_QRV5 } from "./JamezzStyles/data/ribbon/qrRibbon";
import { useKioskJamezzStylesComponentConfiguration } from "./JamezzStyles/data/componentsConfiguration/JSKioskConfig";
import { useQRandWebshopJamezzStylesComponentConfiguration } from "./JamezzStyles/data/componentsConfiguration/JSQRAndWebshopConfig";
import { useSharedJamezzStylesConfiguration } from "./JamezzStyles/data/componentsConfiguration/JSSharedConfig";
import { SystemStyleObject } from "@mui/system";
import { allDesignSuggestions } from "./JamezzStyles/AllDesignSuggestions";
import { parseJSClassName } from "./JamezzStyles/JSClasses";

export type SxProperties = keyof (CSSProperties & SystemStyleObject);
export type CustomizableComponent = {
  className: `${".JS-" | ""}${string}`;
  label: string;
  values?: {
    [cssProp in SxProperties]?: null;
  };
  group?: string;
  groupTranslated?: string;
  JSImage?: {
    name: string;
    res: string;
  }[];
};

export type CustomizableComponentConfiguration = CustomizableComponent[];

export const fontSettings = {
  color: null,
  fontFamily: null,
  fontStyle: null,
  fontWeight: null,
  fontSize: null,
  textTransform: null,
  lineHeight: null,
};

export const buttonSettings = {
  backgroundColor: null,
  ...fontSettings,
};

export const kioskOptionItemSettings = {
  backgroundColor: null,
  border: null,
  borderRadius: null,
  borderColor: null,
  padding: null,
  paddingLeft: null,
  paddingRight: null,
  paddingTop: null,
  paddingBottom: null,
};

export const commonSettings = {
  ...fontSettings,
  backgroundColor: null,
  textDecoration: null,
  lineHeight: null,
  textShadow: null,
  margin: null,
  marginLeft: null,
  marginRight: null,
  marginTop: null,
  marginBottom: null,
  padding: null,
  paddingLeft: null,
  paddingRight: null,
  paddingTop: null,
  paddingBottom: null,
  display: null,
  borderBottom: null,
  borderTop: null,
  borderLeft: null,
  borderRight: null,
  border: null,
  borderRadius: null,
  textAlign: null,
  boxShadow: null,
  zoom: null,
};

function JamezzStylesComponentsConfiguration(): (CustomizableComponent & {
  group: string;
  groupTranslated: string;
})[] {
  const kioskComponents = useKioskJamezzStylesComponentConfiguration();
  const qrAndWebshopComponents = useQRandWebshopJamezzStylesComponentConfiguration();
  const sharedComponents = useSharedJamezzStylesConfiguration();

  const jsComponents = useMemo(() => {
    return ([] as CustomizableComponentConfiguration)
      .concat(kioskComponents, qrAndWebshopComponents, sharedComponents)
      .map((item) => {
        const { group } = parseJSClassName(item.className);
        const groupTranslated = getTranslation(`CustomComponentsHeader.${group}`, "not found");

        return {
          ...item,
          group,
          groupTranslated,
        };
      })
      .sort((a, b) => (`${a.group}#${a.label}` > `${b.group}#${b.label}` ? 1 : -1));
  }, [kioskComponents, qrAndWebshopComponents, sharedComponents]);
  const allClasses = new Set();
  jsComponents.forEach((comp) => {
    if (allClasses.has(comp.className)) {
      console.warn(`Duplicate JS Class ${comp.className}. Fix this`);
    } else {
      allClasses.add(comp.className);
    }
  });
  return jsComponents;
}

export default function CustomComponents() {
  const salesareaType = useWatch({ name: "salesarea_type" });
  const stylingSalesarea = useWatch({ name: "styling_vestiging_id" });
  const [hideCustomComponents, setHideCustomComponents] = useState<boolean>(false);

  const defaultComponents = JamezzStylesComponentsConfiguration();

  //init
  let currentGroupName: string;
  const [selectedGroupVal, setSelectedGroupVal] = useState<string>("none");
  const [showAdminValues, setShowAdminValues] = useState(false);

  //Inactive areas for Kiosk
  const translatedAdminEditMode_Kiosk = getTranslation(
    "CustomComponents_SortingRibbon.Admin_edit_mode.Kiosk",
    "not found"
  );
  const listOfInactiveAreas_Kiosk = translatedAdminEditMode_Kiosk.slice(
    translatedAdminEditMode_Kiosk.indexOf("["),
    translatedAdminEditMode_Kiosk.lastIndexOf("]") + 1
  );
  const translatedAdminEditModeText_Kiosk = translatedAdminEditMode_Kiosk.replace(listOfInactiveAreas_Kiosk, "");

  //Inactive areas for QR
  const translatedAdminEditMode_QR = getTranslation("CustomComponents_SortingRibbon.Admin_edit_mode.QR", "not found");
  const listOfInactiveAreas_QR = translatedAdminEditMode_QR.slice(
    translatedAdminEditMode_QR.indexOf("["),
    translatedAdminEditMode_QR.lastIndexOf("]") + 1
  );
  const translatedAdminEditModeText_QR = translatedAdminEditMode_QR.replace(listOfInactiveAreas_QR, "");

  return (
    <Box sx={{ marginY: 5 }}>
      {/*Live interface environment*/}
      {stylingSalesarea ? null : (
        <LiveInterfaceEnvironment
          defaultComponents={defaultComponents}
          designSuggestions={allDesignSuggestions}
          onClose={() => {
            setHideCustomComponents(false);
          }}
          onOpen={() => {
            setHideCustomComponents(true);
          }}
          salesareaType={salesareaType}
          showAdminValues={showAdminValues}
        />
      )}
      <GridLegacy container>
        <GridLegacy item xs={6}>
          <h3>
            <TranslationWrapper
              tDefault={"Custom Components"}
              tKey={"vestigingen.form.branding.headers.customcomponents"}
            />
          </h3>
        </GridLegacy>

        <AuthenticationWrapper
          child={() => (
            <GridLegacy
              item
              md={6}
              style={{ marginBottom: 10, marginTop: 15, display: "flex", alignItems: "center" }}
              xs={12}
            >
              <Checkbox
                checked={showAdminValues}
                color={"primary"}
                onChange={() => setShowAdminValues((a) => !a)}
                size={"small"}
                style={{ width: 50, height: 50 }}
              />

              {/*Shows admin_edit_mode only on KIOSK*/}
              {(showAdminValues || listOfInactiveAreas_Kiosk === "") && salesareaType === "KIOSK" ? (
                <TranslationWrapper
                  tDefault={"Admin edit mode"}
                  tKey={"CustomComponents_SortingRibbon.Admin_edit_mode.Kiosk"}
                />
              ) : !showAdminValues && salesareaType === "KIOSK" ? (
                <Typography>{translatedAdminEditModeText_Kiosk}</Typography>
              ) : null}

              {/*Shows admin_edit_mode only on QR*/}
              {(showAdminValues || listOfInactiveAreas_QR === "") && salesareaType === "QR" ? (
                <TranslationWrapper
                  tDefault={"Admin edit mode"}
                  tKey={"CustomComponents_SortingRibbon.Admin_edit_mode.QR"}
                />
              ) : !showAdminValues && salesareaType === "QR" ? (
                <Typography>{translatedAdminEditModeText_QR}</Typography>
              ) : null}
            </GridLegacy>
          )}
          permissionKey={"brandingdesign.customcomponents.admineditmode"}
        />
      </GridLegacy>
      {salesareaType === "KIOSK" ? (
        <FilterRibbon
          allSelectedFilterText={"Show all components"}
          areaHighlightBackground={"#000000"}
          areaHighlightBackgroundOpacity={175}
          areaOpacity={100}
          changeSelector={setSelectedGroupVal}
          colorGroup1Background={"#f46e26"}
          colorGroup1Foreground={"#ffffff"}
          colorGroup2Background={"#ffffff"}
          colorGroup2Foreground={"#000000"}
          colorGroup3Background={"#f46e26"}
          colorGroup3Foreground={"#ffffff"}
          inactiveAreaColor={"#FF0000"}
          inactiveAreas={listOfInactiveAreas_Kiosk}
          ribbonComponents={ribbonComponents_KioskV5}
          selectedGroupVal={selectedGroupVal}
          showAdminInfo={showAdminValues}
          showBreadcrumbs={true}
          shownDescription={"side"}
        />
      ) : null}
      {salesareaType === "QR" ? (
        <FilterRibbon
          allSelectedFilterText={"Show all"}
          areaHighlightBackground={"#000000"}
          areaHighlightBackgroundOpacity={175}
          areaOpacity={100}
          changeSelector={setSelectedGroupVal}
          colorGroup1Background={"#f46e26"}
          colorGroup1Foreground={"#ffffff"}
          colorGroup2Background={"#ffffff"}
          colorGroup2Foreground={"#000000"}
          colorGroup3Background={"#f46e26"}
          colorGroup3Foreground={"#ffffff"}
          inactiveAreaColor={"#FF0000"}
          inactiveAreas={listOfInactiveAreas_QR}
          ribbonComponents={ribbonComponents_QRV5}
          selectedGroupVal={selectedGroupVal}
          showAdminInfo={showAdminValues}
          showBreadcrumbs={true}
          shownDescription={"side"}
        />
      ) : null}
      {hideCustomComponents
        ? null
        : defaultComponents.map(({ className, group, groupTranslated, label }, idx) => {
            const translatedLabelName = getTranslation("CustomComponents." + label, "not found");
            const translatedLabelComponentCompressed = translatedLabelName.replace(",", ".").replace(/ /g, "");
            const translatedComponentCompressed = groupTranslated.replace(",", ".").replace(/ /g, "");

            if (
              (translatedLabelComponentCompressed.includes(selectedGroupVal) ||
                translatedLabelName === "not found" ||
                selectedGroupVal === "none") &&
              (translatedComponentCompressed.includes(selectedGroupVal) || selectedGroupVal === "none")
            ) {
              const hasDifferentGroupName = currentGroupName !== group;
              currentGroupName = group;

              return (
                <React.Fragment key={className}>
                  <GridLegacy container style={{ borderTop: "2px solid #fafafa", paddingTop: 0, marginTop: 0 }}>
                    <GridLegacy item sm={3} xs={12}>
                      {hasDifferentGroupName || translatedLabelName.includes(selectedGroupVal) ? (
                        <strong
                          style={{
                            fontSize: "1rem",
                            display: "flex",
                            color:
                              showAdminValues && translatedComponentCompressed.match(/^(?:notfound|\[[^[\]]*])$/)
                                ? "red"
                                : undefined,
                          }}
                        >
                          {showAdminValues ? (
                            <TranslationWrapper tDefault={group} tKey={"CustomComponentsHeader." + group} />
                          ) : (
                            <p>{group}</p>
                          )}
                        </strong>
                      ) : null}
                    </GridLegacy>
                    <GridLegacy item sm={9} xs={12}>
                      <JamezzStylesComponent
                        components={defaultComponents}
                        editMode={"advanced"}
                        index={idx}
                        showAdminValues={showAdminValues}
                        startOpened={false}
                        translatedLabelName={translatedLabelComponentCompressed}
                      />
                    </GridLegacy>
                  </GridLegacy>
                </React.Fragment>
              );
            }
          })}
    </Box>
  );
}
